import {
  BalanceTransaction,
  DataProvider,
  TransactionType,
  TransactionFilters,
} from '@/types/BalanceTransaction'
import {
  extractYearAndMonth,
  getCurrentYearAndMonth,
  getTodaysDateString,
} from '@/utils'
import { defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useMessagesStore } from './messages'
import { LoadingType, useLoadingStore } from './loading'
import { usePlaidItemsStore } from './plaidItem'
import { useCategoriesStore } from './category'

const getTransactionsRequests = new Map<string, Promise<void>>()

type GetTransactionsOptions = {
  throwOnError?: boolean
}

const filterTransactionsByAccountSettings = (
  transactions: BalanceTransaction[],
): BalanceTransaction[] => {
  const plaidItemsStore = usePlaidItemsStore()

  return transactions.filter((transaction) => {
    if (transaction.dataProvider === DataProvider.USER) {
      return true
    }

    const item = plaidItemsStore.items.find(
      (item) =>
        item.accounts?.some((acc) => acc.account_id === transaction.account_id),
    )
    const account = item?.accounts?.find(
      (acc) => acc.account_id === transaction.account_id,
    )

    if (
      account?.showTransactions !== false ||
      (transaction.balanceCategories &&
        transaction.balanceCategories.length > 0)
    ) {
      return true
    }
    return false
  })
}

const calculateMonthlyTotalByType = (
  transactions: Record<string, Record<string, BalanceTransaction>>,
  yearAndMonth: string,
  transactionType: TransactionType,
): number => {
  const monthTransactions = transactions[yearAndMonth]
  if (!monthTransactions) {
    return 0
  }

  const filteredTransactions = filterTransactionsByAccountSettings(
    Object.values(monthTransactions),
  )

  return filteredTransactions
    .filter((transaction: BalanceTransaction) => !transaction.isTransfer)
    .reduce((total: number, transaction: BalanceTransaction) => {
      if (transaction.transactionType === transactionType) {
        return (total += Math.abs(transaction.amount))
      }
      return total
    }, 0)
}

export const useTransactionsStore = defineStore('transactions', {
  state: () => ({
    transactions: {} as Record<string, Record<string, BalanceTransaction>>,
    currentTransaction: null as BalanceTransaction | null,
    currentTransactionOriginalDate: null as string | null,
    selectedTransactions: [] as string[],
    isSelectionMode: false,
  }),
  getters: {
    // ENHANCEMENT - Consider passing the year and month as parameters to this getter
    getTransactionById: (state) => (transactionId: string) => {
      for (const key in state.transactions) {
        if (state.transactions[key][transactionId]) {
          return state.transactions[key][transactionId]
        }
      }
      return null
    },
    getFilteredTransactionsForYearAndMonth:
      (state) => (yearAndMonth: string) => {
        const transactions = state.transactions[`${yearAndMonth}`]
        if (!transactions) {
          return []
        }

        return filterTransactionsByAccountSettings(Object.values(transactions))
      },
    getTransactionsForYearAndMonth: (state) => (yearAndMonth: string) => {
      const transactions = state.transactions[`${yearAndMonth}`]
      if (!transactions) {
        return {}
      }

      const filteredTransactions = filterTransactionsByAccountSettings(
        Object.values(transactions),
      )

      // Convert back to Record format for compatibility
      const filteredTransactionsRecord: Record<string, BalanceTransaction> = {}
      for (const transaction of filteredTransactions) {
        // Find the original key for this transaction
        const originalKey = Object.keys(transactions).find(
          (key) => transactions[key] === transaction,
        )
        if (originalKey) {
          filteredTransactionsRecord[originalKey] = transaction
        }
      }

      return filteredTransactionsRecord
    },
    getMonthlyExpenses: (state) => (yearAndMonth: string) => {
      return calculateMonthlyTotalByType(
        state.transactions,
        yearAndMonth,
        TransactionType.EXPENSE,
      )
    },
    getMonthlyIncome: (state) => (yearAndMonth: string) => {
      return calculateMonthlyTotalByType(
        state.transactions,
        yearAndMonth,
        TransactionType.INCOME,
      )
    },
    getUncategorizedTransactionsCount: (state) => (yearAndMonth: string) => {
      const transactions = state.transactions[`${yearAndMonth}`]
      if (!transactions) {
        return 0
      }

      const filteredTransactions = filterTransactionsByAccountSettings(
        Object.values(transactions),
      )

      return filteredTransactions.filter((transaction: BalanceTransaction) => {
        // Transfers are considered "complete" even without categories
        if (transaction.isTransfer) {
          return false
        }
        return (
          !transaction.balanceCategories ||
          transaction.balanceCategories.length === 0
        )
      }).length
    },
    isEditingCurrentTransaction: (state) => {
      return !!(
        state.currentTransaction && state.currentTransaction.transaction_id
      )
    },
    getFilteredTransactionsForYearAndMonthWithFilters:
      (state) => (yearAndMonth: string, filters: TransactionFilters) => {
        const transactions = state.transactions[`${yearAndMonth}`]
        if (!transactions) {
          return []
        }

        let filteredTransactions = filterTransactionsByAccountSettings(
          Object.values(transactions),
        )

        // Apply additional filters
        if (filters.accounts?.length) {
          filteredTransactions = filteredTransactions.filter((t) =>
            filters.accounts!.includes(t.account_id),
          )
        }

        if (filters.type) {
          filteredTransactions = filteredTransactions.filter(
            (t) => t.transactionType === filters.type,
          )
        }

        if (filters.transfer && filters.transfer !== 'all') {
          const isTransfer = filters.transfer === 'true'
          filteredTransactions = filteredTransactions.filter(
            (t) => t.isTransfer === isTransfer,
          )
        }

        if (filters.status) {
          const isCategorized = filters.status === 'CATEGORIZED'
          filteredTransactions = filteredTransactions.filter((t) => {
            const hasCats =
              t.balanceCategories && t.balanceCategories.length > 0
            if (isCategorized) {
              return hasCats
            } else {
              // Transfers without categories are not considered "uncategorized"
              if (t.isTransfer) {
                return false
              }
              return !hasCats
            }
          })
        }

        if (filters.person) {
          filteredTransactions = filteredTransactions.filter(
            (t) => t.lastUpdatedByProfileId === filters.person,
          )
        }

        if (filters.dataProvider) {
          filteredTransactions = filteredTransactions.filter(
            (t) => t.dataProvider === filters.dataProvider,
          )
        }

        return filteredTransactions
      },
  },
  actions: {
    async loadFromDeviceStorage() {
      const storedTransactions = JSON.parse(
        await this.ionicStorage.get('transactions'),
      )
      if (storedTransactions) {
        this.transactions = storedTransactions
      }
    },
    async getTransactionsForAllItems(
      year: number,
      month: string,
      sync: boolean,
      showLoader: boolean = true,
      options: GetTransactionsOptions = {},
    ) {
      const requestKey = `${year}-${month}-${sync}`
      const loadingStore = useLoadingStore()
      let request = getTransactionsRequests.get(requestKey)

      if (!request) {
        request = (async () => {
          try {
            const response = await this.axios.get(
              `/transactions/${year}/${month}/${sync}`,
            )
            this.transactions = {
              ...this.transactions,
              ...response.data.transactions,
            }
            if (response.data.errors.length > 0) {
              const messagesStore = useMessagesStore()
              messagesStore.messages.push({
                message: 'Your bank account needs updated credentials.',
                duration: 50000,
              })
            }
            await this.ionicStorage.set(
              'transactions',
              JSON.stringify(toRaw(this.transactions)),
            )
          } catch (error) {
            console.error(error)
            throw error
          }
        })()

        getTransactionsRequests.set(requestKey, request)
        request.then(
          () => getTransactionsRequests.delete(requestKey),
          () => getTransactionsRequests.delete(requestKey),
        )
      }

      if (showLoader) {
        loadingStore.startLoading(LoadingType.FetchAllTransactions)
      }

      try {
        await request
      } catch (error) {
        if (options.throwOnError) {
          throw error
        }
      } finally {
        if (showLoader) {
          loadingStore.finishLoading(LoadingType.FetchAllTransactions)
        }
      }
    },
    async fetchTransactionsForItem(
      itemId: string,
      year: string,
      month: string,
      sync: string,
    ) {
      try {
        const response = await this.axios.get(
          `/items/${itemId}/getTransactions/${year}/${month}/${sync}`,
        )
        this.transactions = response.data
      } catch (error) {
        console.error(error)
      }
    },
    async updateCategoriesForTransaction(
      transactionId: string,
      oldCategories: { categoryId: string; amount: number }[],
      newCategories: { categoryId: string; amount: number }[],
    ) {
      let transaction = this.getTransactionById(transactionId)
      if (!transaction) {
        throw new Error("Transaction doesn't exist")
      }

      // Calculate the total amount of the transaction
      const totalAmount = newCategories.reduce(
        (total, category) =>
          total + parseFloat((category.amount || 0).toString()),
        0,
      )

      // Skip validation for transfers with no categories (they're allowed to be uncategorized)
      const isTransferWithNoCategories =
        transaction.isTransfer && newCategories.length === 0
      // Check if the total amount matches the transaction amount
      if (totalAmount !== transaction.amount && !isTransferWithNoCategories) {
        throw new Error(
          'The total amount of categories does not match the transaction amount.',
        )
      }

      transaction.balanceCategories = newCategories
      if (oldCategories.length > 0) {
        const categoriesStore = useCategoriesStore()
        oldCategories.forEach((category) => {
          categoriesStore.removeTransactionFromCategory(
            category.categoryId,
            transactionId,
            extractYearAndMonth(transaction?.date ?? ''),
          )
        })
      }

      newCategories.forEach((category) => {
        const categoriesStore = useCategoriesStore()
        categoriesStore.addTransactionToCategory(
          category.categoryId,
          transactionId,
          extractYearAndMonth(transaction?.date ?? ''),
          category.amount,
        )
      })

      try {
        const response = await this.axios.put(
          `/transaction/${transactionId}`,
          transaction,
        )
        this.transactions[extractYearAndMonth(transaction.date)][
          transactionId
        ] = response.data
      } catch (error) {
        console.error(error)
      }

      const currentYearAndMonth = getCurrentYearAndMonth()
      const transactionYearAndMonth = extractYearAndMonth(transaction.date)
      const isCurrentMonth = currentYearAndMonth === transactionYearAndMonth

      // Refresh all categories if the transaction is not in the current month
      if (!isCurrentMonth) {
        const categoriesStore = useCategoriesStore()
        await categoriesStore.fetchCategories()
      }
    },
    async removeCategoriesForTransaction(transactionId: string) {
      let transaction = this.getTransactionById(transactionId)
      if (!transaction) {
        throw new Error("Transaction doesn't exist")
      }

      transaction.balanceCategories = undefined

      try {
        const response = await this.axios.put(
          `/transaction/${transactionId}/deleteCategoriesForTransaction`,
          transaction,
        )
        this.transactions[extractYearAndMonth(transaction.date)][
          transactionId
        ] = response.data
      } catch (error) {
        console.error(error)
      }
    },
    async createUserTransaction(transactionData: any) {
      const response = await this.axios.post('/transactions', transactionData)
      let dateKey = extractYearAndMonth(response.data.date)
      this.transactions[dateKey] = {
        [response.data.transaction_id]: response.data,
        ...this.transactions[dateKey],
      }
      if (transactionData.categories?.length) {
        transactionData.categories.forEach(
          (category: { categoryId: string; amount: number }) => {
            const categoriesStore = useCategoriesStore()
            categoriesStore.addTransactionToCategory(
              category.categoryId,
              response.data.transaction_id,
              extractYearAndMonth(transactionData?.date ?? ''),
              category.amount,
            )
          },
        )
      }
    },
    async updateUserTransaction(transactionData: any) {
      const response = await this.axios.put(
        `/transaction/temp/${transactionData.transactionId}`,
        transactionData,
      )
      let dateKey = extractYearAndMonth(response.data.date)
      this.transactions[dateKey] = {
        [response.data.transaction_id]: response.data,
        ...this.transactions[dateKey],
      }
      // TODO: Fix this hack
      this.getTransactionsForAllItems(
        parseInt(dateKey.split('-')[0]),
        dateKey.split('-')[1],
        false,
      )
    },
    async deleteCurrentTransaction() {
      if (!this.currentTransaction) {
        throw new Error('No current transaction to delete')
      }
      if (this.currentTransaction.dataProvider !== DataProvider.USER) {
        throw new Error('Cannot delete a transaction that is not from the user')
      }

      let dateKey = extractYearAndMonth(this.currentTransaction.date)
      await this.axios.delete(
        `/transaction/temp/${this.currentTransaction.transaction_id}/${dateKey}`,
      )
      delete this.transactions[dateKey][this.currentTransaction.transaction_id]

      this.currentTransaction = null
      this.currentTransactionOriginalDate = null
    },
    setCurrentTransaction(transactionId?: string) {
      if (transactionId) {
        // Load existing transaction for editing
        const existingTransaction = this.getTransactionById(transactionId)
        if (existingTransaction) {
          this.currentTransaction = { ...existingTransaction }
          this.currentTransactionOriginalDate = existingTransaction.date
        } else {
          console.error(`Transaction with ID ${transactionId} not found`)
          this.currentTransaction = null
          this.currentTransactionOriginalDate = null
        }
      } else {
        // Create new transaction with default values
        this.currentTransaction = {
          name: '',
          transactionType: TransactionType.EXPENSE,
          isTransfer: false,
          date: getTodaysDateString(),
          amount: 0,
          dataProvider: DataProvider.USER,
          cognitoId: '',
          account_id: '',
          iso_currency_code: null,
          unofficial_currency_code: null,
          category: null,
          category_id: null,
          location: {} as any,
          payment_meta: {} as any,
          pending: false,
          pending_transaction_id: null,
          account_owner: null,
          transaction_id: '',
          payment_channel: 'other' as any,
          transaction_code: null,
        } as BalanceTransaction
        this.currentTransactionOriginalDate = null
      }
    },
    updateCurrentTransaction(updates: Partial<BalanceTransaction>) {
      if (this.currentTransaction) {
        this.currentTransaction = { ...this.currentTransaction, ...updates }
      }
    },
    async saveCurrentTransaction() {
      if (!this.currentTransaction) {
        throw new Error('No current transaction to save')
      }

      const isEditing = !!this.currentTransaction.transaction_id

      // For transfers, use the direct amount; otherwise calculate from categories
      let amount: number
      if (this.currentTransaction.isTransfer) {
        // Use the amount directly set on the transaction (for transfers)
        amount = this.currentTransaction.amount || 0
      } else {
        // Calculate amount from categories
        amount =
          this.currentTransaction.balanceCategories?.reduce(
            (acc, category) => acc + category.amount,
            0,
          ) || 0
      }

      const baseTransactionData = {
        name: this.currentTransaction.name,
        date: this.currentTransaction.date,
        transactionType: this.currentTransaction.transactionType,
        isTransfer: this.currentTransaction.isTransfer,
        notes: this.currentTransaction.notes,
        amount:
          this.currentTransaction.transactionType === TransactionType.EXPENSE
            ? Math.abs(amount)
            : -Math.abs(amount),
      }

      if (isEditing) {
        // Update existing transaction
        const transactionData = {
          ...baseTransactionData,
          transactionId: this.currentTransaction.transaction_id,
          oldDate: this.currentTransactionOriginalDate,
          account_id: this.currentTransaction.account_id,
        }
        await this.updateUserTransaction(transactionData)
      } else {
        // Create new transaction
        const transactionData = {
          ...baseTransactionData,
          categories: this.currentTransaction.balanceCategories || [],
          dataProvider: DataProvider.USER,
        }
        await this.createUserTransaction(transactionData)
      }
    },
    clearCurrentTransaction() {
      this.currentTransaction = null
      this.currentTransactionOriginalDate = null
    },
    // Multi-select methods
    enterSelectionMode() {
      this.isSelectionMode = true
      this.selectedTransactions = []
    },
    exitSelectionMode() {
      this.isSelectionMode = false
      this.selectedTransactions = []
    },
    toggleTransactionSelection(transactionId: string) {
      const index = this.selectedTransactions.indexOf(transactionId)
      if (index > -1) {
        this.selectedTransactions.splice(index, 1)
      } else {
        this.selectedTransactions.push(transactionId)
      }
    },
    isTransactionSelected(transactionId: string) {
      return this.selectedTransactions.includes(transactionId)
    },
    async bulkUpdateCategories(
      categories: { categoryId: string; amount: number }[],
    ) {
      try {
        // Call updateCategoriesForTransaction for each selected transaction in parallel
        const updatePromises = this.selectedTransactions.map(
          async (transactionId) => {
            const transaction = this.getTransactionById(transactionId)
            if (!transaction) {
              return {
                success: false,
                transactionId,
                error: 'Transaction not found',
              }
            }

            try {
              const categoriesWithFullAmount = categories.map((cat) => ({
                categoryId: cat.categoryId,
                amount: transaction.amount,
              }))

              const oldCategories = transaction.balanceCategories || []

              await this.updateCategoriesForTransaction(
                transactionId,
                oldCategories,
                categoriesWithFullAmount,
              )

              return { success: true, transactionId }
            } catch (error) {
              console.error(
                `Failed to update transaction ${transactionId}:`,
                error,
              )
              return {
                success: false,
                transactionId,
                error: (error as Error).message,
              }
            }
          },
        )

        const results = await Promise.all(updatePromises)

        const categoriesStore = useCategoriesStore()
        await categoriesStore.fetchCategories()

        return results
      } catch (error) {
        console.error('Bulk update failed:', error)
        throw error
      }
    },
  },
})
